import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Power, PowerOff, Clock, SkipForward, FileCheck, CheckCircle2, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import FacturaForm from '../components/FacturaForm';
import FacturaRecurrenteForm from '../components/FacturaRecurrenteForm';
import { formatMonto, formatFecha } from '../utils/format';
import { pref } from '../utils/moneda';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Plantillas de factura recurrente (#140), como el "Recurring transactions"
// de QuickBooks: proveedor + líneas + cada cuánto. Modo automático genera
// sola la factura de compra (no fiscal); modo recordatorio solo avisa acá
// y reusa la pantalla normal de factura (<FacturaForm/>, vía `extraccion`,
// el mismo mecanismo de precarga que usa la carga por foto/IA) para que el
// usuario confirme el monto y el número real del proveedor antes de crearla.
// ============================================================

const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function describirIntervalo(p) {
  const cada = Number(p.intervalo_cada);
  if (p.intervalo_tipo === 'semanal') {
    const dia = DIAS_SEMANA[Number(p.dia_semana)];
    return cada === 1 ? `Cada semana (${dia})` : `Cada ${cada} semanas (${dia})`;
  }
  return cada === 1 ? `Cada mes, día ${p.dia_mes}` : `Cada ${cada} meses, día ${p.dia_mes}`;
}

function entidadDesdePendiente(p) {
  return { id: p.proveedor_id, nombre: p.proveedor_nombre, rif: p.proveedor_rif, dias_credito_default: p.dias_credito_default };
}

function prellenadoDesdePendiente(p) {
  return {
    fecha: p.proxima_fecha,
    moneda: p.moneda,
    dias_credito: p.dias_credito ?? p.dias_credito_default,
    lineas: (p.lineas || []).map((l) => ({
      producto_id: l.producto_id,
      descripcion: l.descripcion,
      cantidad: l.cantidad,
      precio_unitario: l.precio_unitario,
      exento: !!l.exento
    }))
  };
}

export default function FacturasRecurrentes() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [plantillas, setPlantillas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [confirmando, setConfirmando] = useState(null); // plantilla pendiente que se está generando
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState(null); // resultado del último guardado

  async function cargar() {
    const [pl, pe] = await Promise.all([
      api.get('/compras/recurrentes'),
      api.get('/compras/recurrentes/pendientes')
    ]);
    setPlantillas(pl.data);
    setPendientes(pe.data);
  }

  useEffect(() => {
    if (!empresaActiva) return;
    cargar().catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar las facturas recurrentes'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  function abrirNueva() {
    setEditandoId(null);
    setMostrarForm(true);
  }
  function abrirEdicion(id) {
    setEditandoId(id);
    setMostrarForm(true);
  }
  function cerrarForm() {
    setMostrarForm(false);
    setEditandoId(null);
  }

  async function togglearActivo(p) {
    const accion = p.activo ? 'Inhabilitar' : 'Reactivar';
    if (!window.confirm(`¿${accion} la plantilla "${p.nombre}"?`)) return;
    try {
      await api.patch(`/compras/recurrentes/${p.id}/activo`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || `No se pudo ${accion.toLowerCase()} la plantilla`);
    }
  }

  async function eliminar(p) {
    if (!window.confirm(`¿Eliminar la plantilla "${p.nombre}"? No se puede deshacer.`)) return;
    try {
      await api.delete(`/compras/recurrentes/${p.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la plantilla');
    }
  }

  async function omitir(p) {
    if (!window.confirm(`¿Omitir esta ocurrencia de "${p.nombre}"? No se crea ninguna factura y se pasa a la próxima fecha.`)) return;
    try {
      await api.post(`/compras/recurrentes/${p.id}/omitir`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo omitir');
    }
  }

  async function confirmarGenerada(pendienteId, facturaId) {
    setConfirmando(null);
    try {
      await api.post(`/compras/recurrentes/${pendienteId}/confirmar`, { factura_id: facturaId });
    } catch (err) {
      alert(err.response?.data?.error || 'La factura se creó, pero no se pudo vincular a la plantilla recurrente; revisala a mano.');
    }
    cargar();
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Facturas recurrentes
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={abrirNueva}
            title="Nueva plantilla"
            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md transition-colors"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {pendientes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-3 border-b bg-amber-50 flex items-center gap-2">
            <Clock size={16} className="text-amber-600" />
            <p className="text-sm text-amber-800 font-medium">Pendientes por confirmar</p>
          </div>
          <div className="divide-y">
            {pendientes.map((p) => (
              <div key={p.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{p.nombre}</p>
                  <p className="text-xs text-slate-500">
                    {p.proveedor_nombre} · vence {formatFecha(p.proxima_fecha)}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => setConfirmando(p)}
                    title="Generar factura"
                    className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <FileCheck size={14} /> Generar factura
                  </button>
                  <button onClick={() => omitir(p)} title="Omitir esta vez" className="text-slate-400 hover:text-amber-600 transition-colors">
                    <SkipForward size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {aviso && (
        <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <p className="flex-1">
            Plantilla <strong>"{aviso.nombre}"</strong> guardada.{' '}
            {aviso.facturas_generadas > 0
              ? `Se generó ${aviso.facturas_generadas === 1 ? '1 factura' : `${aviso.facturas_generadas} facturas`} (búscala${aviso.facturas_generadas === 1 ? '' : 's'} en el estado de cuenta del proveedor y en Cuentas por pagar). `
              : ''}
            {aviso.activo
              ? `Próxima ${aviso.modo === 'automatico' ? 'generación automática' : 'fecha'}: ${formatFecha(aviso.proxima_fecha)}.`
              : 'La plantilla llegó a su fecha de fin y quedó inhabilitada.'}
          </p>
          <button onClick={() => setAviso(null)} title="Cerrar" className="text-green-700 hover:text-green-900"><X size={16} /></button>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3">Proveedor</th>
                <th className="p-3">Modo</th>
                <th className="p-3">Repite</th>
                <th className="p-3">Próxima fecha</th>
                <th className="p-3 text-right">Monto estimado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {plantillas.map((p) => (
                <tr key={p.id} className={`border-t hover:bg-slate-50 transition-colors ${aviso?.id === p.id ? 'bg-green-50' : ''}`}>
                  <td className="p-3 font-medium whitespace-nowrap">
                    {p.nombre}
                    {!p.activo && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-normal align-middle">Inhabilitada</span>}
                  </td>
                  <td className="p-3 whitespace-nowrap">{p.proveedor_nombre}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${p.modo === 'automatico' ? 'bg-primary-50 text-primary-700' : 'bg-amber-50 text-amber-700'}`}>
                      {p.modo === 'automatico' ? 'Automática' : 'Recordatorio'}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap text-slate-600">{describirIntervalo(p)}</td>
                  <td className="p-3 whitespace-nowrap">{formatFecha(p.proxima_fecha)}</td>
                  <td className="p-3 text-right whitespace-nowrap">{pref(p.moneda)}{formatMonto(p.monto_estimado)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => abrirEdicion(p.id)} title="Editar" className="text-slate-400 hover:text-primary-600 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => togglearActivo(p)}
                        title={p.activo ? 'Inhabilitar' : 'Reactivar'}
                        className={`transition-colors ${p.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
                      >
                        {p.activo ? <PowerOff size={16} /> : <Power size={16} />}
                      </button>
                      <button onClick={() => eliminar(p)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {plantillas.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-400">
                    No hay facturas recurrentes creadas todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {mostrarForm && (
        <Modal title={editandoId ? 'Editando plantilla' : 'Nueva plantilla de factura recurrente'} onClose={cerrarForm} maxWidth="max-w-3xl">
          <FacturaRecurrenteForm plantillaId={editandoId} onGuardado={(r) => { cerrarForm(); setAviso(r); cargar(); }} onCancelar={cerrarForm} />
        </Modal>
      )}

      {confirmando && (
        <Modal title={`Generar factura — ${confirmando.nombre}`} onClose={() => setConfirmando(null)} maxWidth="max-w-3xl">
          <FacturaForm
            parte="proveedor"
            entidad={entidadDesdePendiente(confirmando)}
            prellenado={prellenadoDesdePendiente(confirmando)}
            onGuardado={(facturaId) => confirmarGenerada(confirmando.id, facturaId)}
            onCancelar={() => setConfirmando(null)}
          />
        </Modal>
      )}
    </div>
  );
}
